import React from "react";
import { FolderSelectionWithRedux } from "../folder-selection/folder-selection";
import { IFolderSelection } from "../folder-selection/folder-navigation";
import { IFolderSelectionSettings } from "../folder-selection/use-folder-selection";
import { Link, mergeStyleSets, SelectionMode, nuOrderTheme as theme } from "@nuorder/components";
import { ItemKind } from "containers/folders/types/base";
import { formatMessage } from "lib/globalize-util";
import { IFolderNavigationBreadcrumbProps } from "../folder-selection/folder-navigation-breadcrumb";
import { DATATEST } from "constants/integration";
import { MaxtItemsDialog } from "../dialogs/max-items";
import { FolderNavigationBreadcrumb } from "./folder-navigation-breadcrumb";

interface IManageDriveItemSelectionProps {
    onChangeSelection?: (target: IFolderSelection[]) => void;
    onClose: (event: any) => void;
    displayMerchantDrive: boolean;
    displayMyDrive: boolean;
    membership: nuorder.merchant.v1.Member;
    mySpace: nuorder.folder.v1.Space;
    merchantSpace: nuorder.folder.v1.Space;
    item: nuorder.folder.v1.Item;
    itemLimit: number;
    settings: IFolderSelectionSettings;
    onUpdateSettings: (settings: Partial<IFolderSelectionSettings>) => void;
    itemKind: ItemKind;
    itemType: string;
}

export const ManageDriveItemSelection: React.FC<IManageDriveItemSelectionProps> = ({
    onChangeSelection,
    onClose,
    onUpdateSettings,
    itemLimit,
    settings,
    displayMerchantDrive,
    membership,
    mySpace,
    merchantSpace,
    item,
    displayMyDrive,
    itemKind,
    itemType,
}) => {
    const classes = React.useMemo(
        () =>
            mergeStyleSets({
                root: {
                    overflow: "hidden",
                },
                footer: {
                    height: 56,
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    paddingRight: 16,
                    borderTop: `1px solid ${theme.palette.neutralLight}`,
                },
            }),
        []
    );

    const [isMaxItemsAlertOpened, setMaxItemsAlertOpened] = React.useState<boolean>(false);
    const handleOkMaxItemsAlert = React.useCallback(() => {
        setMaxItemsAlertOpened(false);
        settings.markAllAsSelected &&
            onUpdateSettings({
                markAllAsSelected: false,
            });
    }, [onUpdateSettings, settings]);

    const handleDisplayAlert = React.useCallback(() => setMaxItemsAlertOpened(true), [setMaxItemsAlertOpened]);

    const handleRenderFolderNavigationBreadcrumb = React.useCallback(
        (props: IFolderNavigationBreadcrumbProps) => {
            return (
                <FolderNavigationBreadcrumb
                    {...props}
                    itemLimit={itemLimit}
                    settings={settings}
                    onClose={onClose}
                    onChangeSelection={onChangeSelection}
                    onUpdateSettings={onUpdateSettings}
                    onSetMaxItemsAlertOpened={setMaxItemsAlertOpened}
                />
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [settings]
    );

    const handleUpdateSettings = React.useCallback(
        (_settings: Partial<IFolderSelectionSettings>) => {
            if (_settings.hasOwnProperty("itemSelected")) {
                onChangeSelection(_settings.itemSelected as IFolderSelection[]);
            }
            onUpdateSettings(_settings);
        },
        [onUpdateSettings, onChangeSelection]
    );

    return (
        <div className={classes.root} data-test={DATATEST.FOLDERS.ITEM_SELECTOR}>
            <FolderSelectionWithRedux
                displayMerchantDrive={displayMerchantDrive}
                displayMyDrive={displayMyDrive}
                membership={membership}
                mySpace={mySpace}
                merchantSpace={merchantSpace}
                item={item}
                settings={settings}
                onUpdateSettings={handleUpdateSettings}
                selectionMode={SelectionMode.multiple}
                selectionKind={itemKind}
                onRenderWayfinding={handleRenderFolderNavigationBreadcrumb}
                onDisplayAlert={handleDisplayAlert}
                noMargin={true}
                itemLimit={itemLimit}
            />
            <div className={classes.footer}>
                <Link href="" onClick={onClose} data-test={DATATEST.FOLDERS.ITEM_PICKER.DONE_BTN}>
                    {formatMessage("Done")}
                </Link>
            </div>
            <MaxtItemsDialog
                open={isMaxItemsAlertOpened}
                itemLimit={itemLimit}
                onOk={handleOkMaxItemsAlert}
                itemType={itemType}
            />
        </div>
    );
};
